import { AppstoreFilled, InfoCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { Badge, Button, Checkbox, Col, DatePicker, Dropdown, Form, Input, message, Modal, Popconfirm, Popover, Radio, Row, Select, Space, Spin, Tabs, Tag, Tooltip, Typography, Upload } from 'antd';
import dayjs from 'dayjs';
import { AlignJustify, Copy, Edit, File, FileText, List, MoreVertical, Paperclip, Plus, Trash2, Undo, Upload as UploadIcon, X } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReadOnlyEditor from '../../components/elements/ReadOnlyRichTextEditor';
import RichTextEditor from '../../components/elements/RichTextEditor';
import { Attachment, ScheduledAndPriorityUpdate } from '../../api/KanbanTaskApiClient';
import AssigneeSelector from '../../components/elements/KanbanBoard/AsigneeSelection';
import RichTextComments from '../../components/elements/KanbanBoard/RichTextComments/RichTextComments';
import '../../components/elements/KanbanBoard/RichTextComments/RichTextComments.css';
import TaskCustomFields from '../../components/elements/KanbanBoard/TaskCustomFields/TaskCustomFields';
import { getUserComponent } from '../../helpers/user';
import { KanbanTask, KanbanTaskChange, KanbanTaskComment, makeSummary, NewKanbanTaskComment, } from '../../stores/KanbanTaskStores';
import { useStore } from '../../stores/root-store-context';
import InlineEditableField from './InlineEditableField';
import './KanbanTasks.css';
import ListView from './ListView';
import TaskCard from './TaskCard';
import TimeTabContent from './TimeTabContent';
import { ColumnDropTarget, DropTarget, TaskForm, ViewType } from './types';
import { getPriorityColor, getPriorityIcon, priorityOptions, safeParseEditorData } from './useTaskHelpers';
import InlineTaskForm from './InlineTaskForm';
import { jwtDecode } from 'jwt-decode';
import { ExportTimeLogsParams } from '../../stores/KanbanTaskStores';
import i18n from '../../i18n';
import { Roles } from '../../enums/Roles';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Dragger } = Upload;

interface KanbanTasksProps {
  isShowInternalLoader: boolean;
  isAnonymous?: boolean;
  getTaskByIdProp?: (id: string) => Promise<any>;
  getTaskCommentsProp?: (id: string) => Promise<any>;
  getTaskChangesProp?: (taskIdOrPublicId: string) => Promise<any[]>;
  getTaskTimeMetricsProp?: (taskId: string) => Promise<any>;
  getTaskAttachmentsProp?: (taskId: string) => Promise<Attachment[]>;
  uploadTaskAttachmentProp?: (taskId: string, file: File, confirmation?: boolean) => Promise<Attachment | null>;
  createCommentProp?: (comment: NewKanbanTaskComment) => Promise<KanbanTaskComment>;
  updateTaskScheduledAndPriorityProp?: (taskId: string, scheduledAndPriority: ScheduledAndPriorityUpdate) => Promise<void>;
}

const KanbanTasks = observer(({
  isShowInternalLoader,
  isAnonymous = false,
  getTaskByIdProp,
  getTaskChangesProp,
  getTaskAttachmentsProp,
  uploadTaskAttachmentProp,
  updateTaskScheduledAndPriorityProp
}: KanbanTasksProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { projectSlug, taskId } = useParams<{ projectSlug: string; taskId: string }>();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { applicationStore, projectsStore, kanbanTaskStore, employeesStore, anonymousStore } = useStore();
  const [timeLoggingForm] = Form.useForm();
  const [estimatedTimeForm] = Form.useForm();
  const [isLoggingTime, setIsLoggingTime] = useState(false);
  const [isEditingEstimatedTime, setIsEditingEstimatedTime] = useState(false);
  const [showDeletedTasks, setShowDeletedTasks] = useState(false);

  const getTaskTimeMetrics = isAnonymous ? anonymousStore.getTaskTimeMetrics : kanbanTaskStore.getTaskTimeMetrics;

  const fetchTaskTimeData = useCallback(async (taskId: string) => {
    try {
      await getTaskTimeMetrics(taskId);
      if (isAnonymous) {
        await anonymousStore.getTaskTimeLogs(taskId);
      } else {
        await kanbanTaskStore.getTaskTimeLogs(taskId);
      }
    } catch (error) {
      console.error('Error fetching time data:', error);
      message.error(t('FailedToLoadTimeData'));
    }
  }, [kanbanTaskStore, t]);

  const handleLogTime = async (values: any) => {
    if (!selectedTask) return;

    try {
      let timeValue;
      if (!values.time) {
        timeValue = '00:00';
      } else if (typeof values.time === 'string') {
        timeValue = values.time;
      } else if (values.time && typeof values.time.format === 'function') {
        timeValue = values.time.format('HH:mm');
      } else {
        message.error(t('InvalidTimeFormat'));
        return;
      }

      const { description } = values;
      const endDateStr = values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined;

      if (isAnonymous) {
        const success = await anonymousStore.logTimeForTask(
          selectedTask.id.toString(),
          timeValue,
          description,
          undefined,
          endDateStr
        );

        if (success) {
          message.success(t('TimeLogged'));
          timeLoggingForm.resetFields();
          setIsLoggingTime(false);
          await fetchTaskTimeData(selectedTask.id.toString());
        }
      } else {
        const success = await kanbanTaskStore.logTimeForTask(
          selectedTask.id.toString(),
          timeValue,
          description,
          undefined,
          endDateStr
        );

        if (success) {
          message.success(t('TimeLogged'));
          timeLoggingForm.resetFields();
          setIsLoggingTime(false);
          await fetchTaskTimeData(selectedTask.id.toString());
        }
      }
    } catch (error) {
      console.error('Error logging time:', error);
      message.error(t('FailedToLogTime'));
    }
  };

  const handleUpdateEstimatedTime = async (values: any) => {
    if (!selectedTask) return;

    try {
      if (!values.estimatedTime) {
        message.error(t('InvalidTimeValue'));
        return;
      }

      let estimatedTime;
      if (typeof values.estimatedTime === 'string') {
        estimatedTime = values.estimatedTime;
      } else if (values.estimatedTime && typeof values.estimatedTime.format === 'function') {
        estimatedTime = values.estimatedTime.format('HH:mm');
      } else {
        estimatedTime = '00:00';
      }

      const success = await kanbanTaskStore.updateEstimatedTime(
        selectedTask.id.toString(),
        estimatedTime
      );

      if (success) {
        message.success(t('EstimatedTimeUpdated'));
        setIsEditingEstimatedTime(false);
        await getTaskTimeMetrics(selectedTask.id.toString());
      }
    } catch (error) {
      console.error('Error updating estimated time:', error);
      message.error(t('FailedToUpdateEstimatedTime'));
    }
  };

  const [viewMode, setViewMode] = useState<ViewType>('kanban');
  const [selectedProject, setSelectedProject] = useState<string | undefined>(projectId || undefined);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');
    if (viewParam === 'list' || viewParam === 'kanban') {
      setViewMode(viewParam);
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const priority = params.get('priority');
    if (priority) {
      setFilterPriority(parseInt(priority, 10));
    }
    const assignee = params.get('assignee');
    if (assignee) {
      setFilterAssignee(assignee);
    }
    const createdDate = params.get('createdDate');
    if (createdDate) {
      const [start, end] = createdDate.split('_');
      setFilterCreatedDate([start ? dayjs(start) : null, end ? dayjs(end) : null]);
    }
    const startDate = params.get('startDate');
    if (startDate) {
      const [start, end] = startDate.split('_');
      setFilterStartDate([start ? dayjs(start) : null, end ? dayjs(end) : null]);
    }
    const dueDate = params.get('dueDate');
    if (dueDate) {
      const [start, end] = dueDate.split('_');
      setFilterDueDate([start ? dayjs(start) : null, end ? dayjs(end) : null]);
    }
  }, [location.search]);

  const handleViewChange = (newView: ViewType) => {
    setViewMode(newView);
    const params = new URLSearchParams(location.search);
    params.set('view', newView);
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleFilterPriorityChange = (value: number | null) => {
    setFilterPriority(value);
    const params = new URLSearchParams(location.search);
    if (value !== null) {
      params.set('priority', value.toString());
    } else {
      params.delete('priority');
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleFilterAssigneeChange = (value: string | null) => {
    setFilterAssignee(value);
    const params = new URLSearchParams(location.search);
    if (value !== null) {
      params.set('assignee', value);
    } else {
      params.delete('assignee');
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleFilterCreatedDateChange = (value: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setFilterCreatedDate(value);
    const params = new URLSearchParams(location.search);
    if (value !== null && (value[0] || value[1])) {
      const start = value[0]?.toISOString() || '';
      const end = value[1]?.toISOString() || '';
      params.set('createdDate', `${start}_${end}`);
    } else {
      params.delete('createdDate');
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleFilterStartDateChange = (value: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setFilterStartDate(value);
    const params = new URLSearchParams(location.search);
    if (value !== null && (value[0] || value[1])) {
      const start = value[0]?.toISOString() || '';
      const end = value[1]?.toISOString() || '';
      params.set('startDate', `${start}_${end}`);
    } else {
      params.delete('startDate');
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleFilterDueDateChange = (value: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setFilterDueDate(value);
    const params = new URLSearchParams(location.search);
    if (value !== null && (value[0] || value[1])) {
      const start = value[0]?.toISOString() || '';
      const end = value[1]?.toISOString() || '';
      params.set('dueDate', `${start}_${end}`);
    } else {
      params.delete('dueDate');
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    if (!token) return;

    try {
      const decoded: any = jwtDecode(token);
      setSelectedProject(decoded.projectId);
    } catch (err) {
      console.error("JWT decode error:", err);
    }
  }, [location.search]);

  const isReadOnly = useMemo(() => {
    if (isAnonymous) return false;

    if (!applicationStore.user) return true;

    const hasGlobalAdminOrOwner = applicationStore.user.roles?.some(
      r => r.id == Roles.Owner || r.id == Roles.Admin
    ) ?? false;
    if (hasGlobalAdminOrOwner) return false;

    if (!projectsStore.project) return true;

    const projectMembers = projectsStore.project.members ||
      [];

    const userMembership = Array.isArray(projectMembers) ?
      projectMembers.find(member =>
        member.id === applicationStore.user?.id
      ) : null;

    const isPublic = projectsStore.project?.isPublish ?? false;

    if (!userMembership) {
      if (!isPublic) return true;
      return false;
    }

    const roleTitle = userMembership.role?.title ?? '';
    const isViewer = roleTitle.toLowerCase() === 'viewer' ||
      Number(userMembership.role?.id) === Roles.Viewer;

    return isViewer;
  }, [applicationStore.user, projectsStore.project, projectId, isAnonymous]);

  useEffect(() => {
    const loadProjectBySlug = async () => {
      if (projectSlug) {
        try {
          const project = await projectsStore.getBySlug(projectSlug);
          if (project) {
            setProjectId(project.id.toString());
            setSelectedProject(project.id.toString());
          } else {
            console.error(`Project not found: ${projectSlug}`);
            message.error(t('ProjectNotFound'));
          }
        } catch (err) {
          console.error('Failed to load project by slug', err);
          message.error(t('FailedToLoadProject'));
        }
      }
    };

    loadProjectBySlug();
  }, [projectSlug, projectsStore, t]);

  useEffect(() => {
    (async () => {
      try {
        await projectsStore.getAll();
      } catch (err) {
        console.error('Failed to load projects', err);
      }
    })();
    employeesStore.getUsers().catch((err) => console.error('Failed to load users', err));
  }, [employeesStore, projectsStore]);

  useEffect(() => {
    if (selectedProject || isAnonymous) {
      projectsStore.getById(selectedProject!).catch(console.error);
      const projIdNumber = Number(selectedProject);
      const store = isAnonymous ? anonymousStore : kanbanTaskStore;
      if (!Number.isNaN(projIdNumber)) {
        if (viewMode === 'kanban' && !isAnonymous) {
          kanbanTaskStore.getKanbanViewTasks(selectedProject!).catch(console.error);
        } else {
          store.getProjectTasks(selectedProject!).catch(console.error);
        }
        store.getProjectStatuses(selectedProject!).catch(console.error);
        if (showDeletedTasks && !isAnonymous) {
          store.getDeletedProjectTasks(selectedProject!).catch(console.error);
        }
      }
    }
  }, [selectedProject, projectsStore, kanbanTaskStore, anonymousStore, isAnonymous, showDeletedTasks, viewMode]);
  
  useEffect(() => {
    if (projectId && projectId !== selectedProject) {
      setSelectedProject(projectId);
    }
  }, [projectId, selectedProject]);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState<number | null>(null);
  const [filterAssignee, setFilterAssignee] = useState<string | null>(null);
  const [filterCreatedDate, setFilterCreatedDate] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [filterStartDate, setFilterStartDate] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [filterDueDate, setFilterDueDate] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const [inlineAddingTaskInStatus, setInlineAddingTaskInStatus] = useState<string | null>(null);

  const handleQuickAddTask = async (statusId: string, title: string = '', description: string = ''): Promise<void> => {
    try {
      let resolvedProjectId: number | null = null;
      let resolvedUserId: number | null = null;

      if (isAnonymous) {
        const query = new URLSearchParams(window.location.search);
        const tokenFromUrl = query.get("token");

        function parseJwt(token: string) {
          try {
            const base64Payload = token.split('.')[1];
            const payload = atob(base64Payload);
            return JSON.parse(payload);
          } catch {
            return null;
          }
        }

        const jwtPayload = tokenFromUrl ? parseJwt(tokenFromUrl) : null;
        resolvedProjectId = Number(jwtPayload?.projectId);
        resolvedUserId = 5;

        if (!resolvedProjectId) {
          message.error('Anonymous token missing projectId');
          return;
        }
      } else {
        const projectIdNumber = Number(selectedProject);
        if (Number.isNaN(projectIdNumber)) {
          message.error('Invalid project ID');
          return;
        }
        resolvedProjectId = projectIdNumber;

        const currentUserId = applicationStore.user?.id || employeesStore.user?.id;
        if (!currentUserId || Number.isNaN(Number(currentUserId))) {
          message.error('Invalid user ID.');
          return;
        }
        resolvedUserId = Number(currentUserId);
      }

      const store = isAnonymous ? anonymousStore : kanbanTaskStore;

      await store.createTask({
        title,
        description: description
          ? JSON.stringify({ blocks: [{ type: "paragraph", data: { text: description } }] })
          : "",
        projectId: resolvedProjectId,
        statusId: Number(statusId),
        priority: 1,
        createdByUserId: resolvedUserId,
        startDate: dayjs().format('YYYY-MM-DD HH:mm'),
      });

      message.success(t('TaskCreated'));
      setInlineAddingTaskInStatus(null);
    } catch (error) {
      console.error(error);
      message.error(t('FailedToCreateTask'));
    }
  };

  const handleClearFilters = () => {
    setFilterPriority(null);
    setFilterAssignee(null);
    setFilterCreatedDate(null);
    setFilterStartDate(null);
    setFilterDueDate(null);
    const params = new URLSearchParams(location.search);
    params.delete('priority');
    params.delete('assignee');
    params.delete('createdDate');
    params.delete('startDate');
    params.delete('dueDate');
    navigate({ search: params.toString() }, { replace: true });
  };

  const matchesSearchQuery = (task: KanbanTask, query: string) => {
    if (!query) return true;

    const q = query.toLowerCase();

    return (
      task.title?.toLowerCase().includes(q) ||
      task.publicId?.toLowerCase().includes(q)
    );
  };

  const getFilteredTasks = useCallback(
    (statusId: string): KanbanTask[] => {
      const tasks = showDeletedTasks ? kanbanTaskStore.deletedProjectTasks : (isAnonymous ? anonymousStore.kanbanTasks : (viewMode === 'kanban' ? kanbanTaskStore.kanbanViewTasks : kanbanTaskStore.projectTasks)) || [];
      return tasks
        .filter((task) => task.statusId === statusId)
        .filter((task) => matchesSearchQuery(task, searchQuery))
        .filter((task) => (filterPriority === null ? true : task.priority === filterPriority))
        .filter((task) => {
          if (!filterAssignee) return true;
          return String(task.assigneeId) === String(filterAssignee);
        })
        .filter((task) =>
          isInDateRange(task.createdDate, filterCreatedDate)
        )
        .filter((task) =>
          isInDateRange(task.startDate, filterStartDate)
        )
        .filter((task) =>
          isInDateRange(task.dueDate, filterDueDate)
        )
        .sort((a, b) => a.index - b.index).map((task) => {
          const assigned = employeesStore.users.find((user) => String(user.id) === String(task.assigneeId));
          return {
            ...task,
            assignee: assigned || undefined,
          }
        });
    },
    [
      // Data
      anonymousStore.kanbanTasks,
      kanbanTaskStore.projectTasks,
      kanbanTaskStore.kanbanViewTasks,
      kanbanTaskStore.deletedProjectTasks,
      anonymousStore.projectStatuses,
      kanbanTaskStore.projectStatuses,

      // Flags
      isAnonymous,
      showDeletedTasks,
      viewMode,

      // Filters
      searchQuery,
      filterPriority,
      filterAssignee,
      filterCreatedDate,
      filterStartDate,
      filterDueDate,
    ]
  );

  const isInDateRange = (
    date: string | dayjs.Dayjs | null | undefined,
    range: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  ) => {
    if (!range || (!range[0] && !range[1])) return true;
    if (!date) return false;

    const value = dayjs(date);
    const [from, to] = range;

    if (from && value.isBefore(from.startOf('day'))) return false;
    if (to && value.isAfter(to.endOf('day'))) return false;

    return true;
  };

  const getAllFilteredTasks = useCallback(
    (): KanbanTask[] => {
      const tasks = showDeletedTasks ? kanbanTaskStore.deletedProjectTasks : (isAnonymous ? anonymousStore.kanbanTasks : (viewMode === 'kanban' ? kanbanTaskStore.kanbanViewTasks : kanbanTaskStore.projectTasks)) || [];
      return tasks
        .filter((task) => matchesSearchQuery(task, searchQuery))
        .filter((task) => (filterPriority === null ? true : task.priority === filterPriority))
        .filter((task) => {
          if (!filterAssignee) return true;
          return String(task.assigneeId) === String(filterAssignee);
        });
    },
    [anonymousStore.kanbanTasks, kanbanTaskStore.projectTasks, kanbanTaskStore.kanbanViewTasks, kanbanTaskStore.deletedProjectTasks, searchQuery, filterPriority, filterAssignee, showDeletedTasks, viewMode]
  );

  const [isAddColumnModalVisible, setIsAddColumnModalVisible] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [isFinalStatus, setIsFinalStatus] = useState(false);
  const [editedIsFinalStatus, setEditedIsFinalStatus] = useState<boolean>(false);
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [editedColumnName, setEditedColumnName] = useState('');

  const handleAddColumn = async () => {
    if (!newColumnName.trim() || !selectedProject) return;

    const store = isAnonymous ? anonymousStore : kanbanTaskStore;

    const result = await store.createStatus(newColumnName, selectedProject, isFinalStatus);
    if (result) {
      message.success(t('ColumnCreated'));
      setNewColumnName('');
      setIsAddColumnModalVisible(false);
      setIsFinalStatus(false);
    }
  };

  const handleEditColumn = (statusId: string, currentName: string, isFinalStatus: boolean) => {
    setEditingColumnId(statusId);
    setEditedColumnName(currentName);
    setEditedIsFinalStatus(isFinalStatus);
  };

  const handleSaveColumnName = async () => {
    if (!editingColumnId || !editedColumnName.trim()) return;

    try {
      const store = isAnonymous ? anonymousStore : kanbanTaskStore;
      const result = await store.updateStatus(editingColumnId, editedColumnName, editedIsFinalStatus);
      if (projectId) {
        store.getProjectStatuses(projectId);
      }
      if (result) {
        message.success(t('ColumnUpdated'));
      }
    } catch (error) {
      console.error('Error updating column name:', error);
      message.error(t('FailedToUpdateColumn'));
    } finally {
      setEditingColumnId(null);
      setEditedColumnName('');
    }
  };

  const handleCancelEditColumn = () => {
    setEditingColumnId(null);
    setEditedColumnName('');
    setEditedIsFinalStatus(false);
  };

  const handleDeleteColumn = async (statusId: string) => {
    const result = await kanbanTaskStore.deleteStatus(statusId);
    if (result) {
      message.success(t('ColumnDeleted'));
    }
  };

  const [isTaskDetailsModalVisible, setIsTaskDetailsModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<KanbanTask | null>(null);
  const [taskComments, setTaskComments] = useState<KanbanTaskComment[]>([]);
  const [activeTab, setActiveTab] = useState<'comments' | 'changes' | 'time'>('comments');

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescriptionData, setEditedDescriptionData] = useState<any>({ blocks: [] });
  const [isEditingDatesPriority, setIsEditingDatesPriority] = useState(false);
  const [editedDatesPriority, setEditedDatesPriority] = useState<{ startDate: any; dueDate: any; priority: number }>({
    startDate: null,
    dueDate: null,
    priority: 1,
  });
  const [isEditingAssignee, setIsEditingAssignee] = useState(false);
  const [editedAssigneeId, setEditedAssigneeId] = useState<string | undefined>(undefined);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [editedStatusId, setEditedStatusId] = useState<string | undefined>(undefined);
  const [isCommentLoading, setIsCommentLoading] = useState(false);

  const getTaskByIdAdapter = getTaskByIdProp || kanbanTaskStore.getTaskById;

  const handleShowTaskDetails = (task: KanbanTask) => {
    const id = task.id ? task.id.toString() : task.publicId?.toString();
    if (!id) return;

    getTaskByIdAdapter(id).then((newtask) => {
      setSelectedTask(newtask);
      setIsTaskDetailsModalVisible(true);

      const taskIdentifier = task.publicId || task.id;

      let newUrl: string;

      if (getTaskByIdProp) {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        newUrl = `/anonymous/project/tasks/${taskIdentifier}?token=${token}`;
      } else {
        newUrl = `/${applicationStore.company?.Tenant}/projects/${projectSlug}/tasks/${taskIdentifier}`;
      }
      navigate(newUrl, { replace: false });
    });
  };

  const handleCloseTaskDetails = () => {
    setIsTaskDetailsModalVisible(false);
    setSelectedTask(null);

    let tasksListUrl: string;

    if (getTaskByIdProp) {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");
      tasksListUrl = `/anonymous/project/tasks/?token=${token}`;
    } else {
      tasksListUrl = `/${applicationStore.company?.Tenant}/projects/${projectSlug}/tasks`;
    }
    navigate(tasksListUrl, { replace: true });
  };

  const copyTaskLink = () => {
    if (selectedTask) {

      navigator.clipboard.writeText(window.location.href)
        .then(() => {
          message.success(t('TaskLinkCopied'));
        })
        .catch(() => {
          message.error(t('FailedToCopyLink'));
        });
    }
  };

  useEffect(() => {
    const match = location.pathname.match(/\/tasks\/([^\/]+)$/);

    if (!match?.[1]) {
      if (isTaskDetailsModalVisible) {
        setIsTaskDetailsModalVisible(false);
        setSelectedTask(null);
      }
      return;
    }

    const taskIdentifier = match[1];

    if (
      isTaskDetailsModalVisible &&
      selectedTask &&
      (
        selectedTask.id?.toString() === taskIdentifier ||
        selectedTask.publicId === taskIdentifier
      )
    ) {
      return;
    }

    let cancelled = false;

    const loadTask = async () => {
      try {
        let task: KanbanTask | null = null;

        if (isAnonymous) {
          task = await kanbanTaskStore.ApiStore.AnonymousApiClient.getTask(taskIdentifier);
        } else {
          if (!projectId) return;

          task = await kanbanTaskStore.ApiStore.KanbanTaskApiClient.getTaskDetails(
            taskIdentifier,
            projectId
          );
        }

        if (cancelled) return;

        if (!task) {
          message.error(t('TaskNotFound'));

          const fallbackUrl = isAnonymous
            ? `/anonymous/project/tasks${location.search}`
            : `/${applicationStore.company?.Tenant}/projects/${projectSlug}/tasks`;

          navigate(fallbackUrl, { replace: true });
          return;
        }

        setSelectedTask(task);
        setIsTaskDetailsModalVisible(true);

      } catch (error) {
        if (cancelled) return;

        console.error('Failed to load task:', error);
        message.error(t('FailedToLoadTask'));

        const fallbackUrl = isAnonymous
          ? `/anonymous/project/tasks${location.search}`
          : `/${applicationStore.company?.Tenant}/projects/${projectSlug}/tasks`;

        navigate(fallbackUrl, { replace: true });
      }
    };

    loadTask();

    return () => {
      cancelled = true;
    };

  }, [ location.pathname, projectId, isAnonymous ]);

  const getTaskComments = isAnonymous ? anonymousStore.getTaskComments : kanbanTaskStore.ApiStore.KanbanTaskApiClient.getTaskComments;
  const getTaskChanges = getTaskChangesProp || kanbanTaskStore.getTaskChanges;
  const getTaskAttachments = getTaskAttachmentsProp || kanbanTaskStore.getTaskAttachments;
  const uploadTaskAttachment = uploadTaskAttachmentProp || (isAnonymous ? anonymousStore.uploadTaskAttachment : kanbanTaskStore.uploadTaskAttachment);
  const updateTaskScheduledAndPriority = updateTaskScheduledAndPriorityProp || kanbanTaskStore.updateTaskScheduledAndPriority;

  useEffect(() => {
    if (selectedTask) {
      setActiveTab('comments');
      getTaskComments(selectedTask.id)
        .then(setTaskComments)
        .catch((error) => {
          console.error(error);
          message.error(t('FailedToLoadComments'));
        });
      getTaskChanges(selectedTask.id.toString()).catch((error) => {
        console.error(error);
        message.error(t('FailedToLoadChanges'));
      });
      const parsed = safeParseEditorData(selectedTask.description);
      setEditedDescriptionData(parsed);
      setIsEditingDescription(false);
      setEditedTitle(selectedTask.title);
      setIsEditingTitle(false);
      setEditedDatesPriority({
        startDate: selectedTask.startDate ? dayjs(selectedTask.startDate) : dayjs(),
        dueDate: selectedTask.dueDate ? dayjs(selectedTask.dueDate) : null,
        priority: selectedTask.priority,
      });
      setIsEditingDatesPriority(false);
      setEditedAssigneeId(selectedTask.assigneeId);
      setIsEditingAssignee(false);
      setEditedStatusId(selectedTask.statusId);
      setIsEditingStatus(false);

      fetchTaskTimeData(selectedTask.id.toString());

      getTaskAttachments(selectedTask.id.toString())
        .then((atts) => setAttachments([...atts]))
        .catch((error) => {
          console.error(error);
          message.error(t('FailedToLoadAttachments'));
        });
      setUploadFileList([]);
    } else {
      setTaskComments([]);
      setAttachments([]);
    }
  }, [selectedTask, t, kanbanTaskStore]);

  const handleDeleteTask = async (taskId: string): Promise<void> => {
    try {
      if (showDeletedTasks) {
        await kanbanTaskStore.deleteTaskPermanent(taskId);
      }
      else {
        await kanbanTaskStore.deleteTask(taskId);
      }

      message.success(t('TaskDeleted'));

      setIsTaskDetailsModalVisible(false);
      setSelectedTask(null);

      let tasksListUrl: string;

      if (getTaskByIdProp) {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        tasksListUrl = `/anonymous/project/tasks/?token=${token}`;
      } else {
        tasksListUrl = `/${applicationStore.company?.Tenant}/projects/${projectSlug}/tasks`;
      }

      navigate(tasksListUrl, { replace: true });

    } catch (error) {
      console.error(error);
      message.error(t('FailedToDeleteTask'));
    }
  };

  const handleSaveTitle = async (newTitle: string) => {
    if (!selectedTask) return;
    try {
      const updatedTask = { ...selectedTask, title: newTitle };
      if (isAnonymous) {
        await anonymousStore.updateTaskTitle(selectedTask.id, newTitle);
      } else {
        await kanbanTaskStore.updateTaskTitle(selectedTask.id, newTitle);
      }
      setSelectedTask(updatedTask);
      message.success(t('TaskTitleUpdated'));
    } catch (err) {
      console.error(err);
      message.error(t('FailedToUpdateTaskTitle'));
    }
  };

  const handleSaveDescription = async () => {
    if (!selectedTask) return;
    try {
      const descriptionString = JSON.stringify(editedDescriptionData);
      const summaryString = makeSummary(descriptionString);

      const updatedTask = { ...selectedTask, description: descriptionString, summary: summaryString, assignee: undefined, comments: undefined };

      if (isAnonymous) {
        await anonymousStore.updateTaskDescription(selectedTask.id, descriptionString);
      } else {
        await kanbanTaskStore.updateTaskDescription(selectedTask.id, descriptionString);
      }
      setSelectedTask(updatedTask);
      setIsEditingDescription(false);
      message.success(t('DescriptionUpdated'));
    } catch (err) {
      console.error(err);
      message.error(t('FailedToUpdateDescription'));
    }
  };

  const handleSaveDatesPriority = async () => {
    if (!selectedTask) return;
    const scheduledAndPriorityUpdate = {
      startDate: editedDatesPriority.startDate
        ? editedDatesPriority.startDate.format("YYYY-MM-DD")
        : null,
      dueDate: editedDatesPriority.dueDate
        ? editedDatesPriority.dueDate.format("YYYY-MM-DD")
        : null,
      priority: editedDatesPriority.priority,
    };
    const updatedTask = {
      ...selectedTask,
      startDate: scheduledAndPriorityUpdate.startDate,
      dueDate: scheduledAndPriorityUpdate.dueDate,
      priority: scheduledAndPriorityUpdate.priority,
    };
    try {
      await updateTaskScheduledAndPriority(selectedTask.id, scheduledAndPriorityUpdate);
      setSelectedTask(updatedTask);
      setIsEditingDatesPriority(false);
      message.success(t('TaskUpdated'));
    } catch (err) {
      console.error(err);
      message.error(t('FailedToUpdateTask'));
    }
  };

  const handleSaveAssignee = async () => {
    if (!selectedTask) return;

    setIsSavingAssignee(true);

    try {
      const updatedTask = { ...selectedTask, assigneeId: editedAssigneeId };
      if (
        updatedTask.assignee &&
        typeof updatedTask.assignee === 'object' &&
        'firstName' in updatedTask.assignee &&
        'userRoles' in updatedTask.assignee
      ) {
        delete (updatedTask.assignee as any).userRoles;
      }

      await kanbanTaskStore.updateTaskAssignee(selectedTask.id, editedAssigneeId);

      const refreshedTask = await kanbanTaskStore.ApiStore.KanbanTaskApiClient.getTaskDetails(selectedTask.id, projectId || '');

      if (refreshedTask) {
        setSelectedTask(refreshedTask);
        message.success(t('TaskAssigneeUpdated'));
      } else {
        setSelectedTask(updatedTask);
        message.warning(t('TaskUpdated') + '. ' + t('RefreshForDetails'));
      }

      setIsEditingAssignee(false);
    } catch (err) {
      console.error(err);
      message.error(t('FailedToUpdateAssignee'));
    } finally {
      setIsSavingAssignee(false);
    }
  };

  const assignCurrentUser = async () => {
    if (!selectedTask || !applicationStore.user?.id) return;

    setIsSavingAssignee(true);

    try {
      const updatedTask = { ...selectedTask, assigneeId: applicationStore.user?.id };
      if (
        updatedTask.assignee &&
        typeof updatedTask.assignee === 'object' &&
        'firstName' in updatedTask.assignee &&
        'userRoles' in updatedTask.assignee
      ) {
        delete (updatedTask.assignee as any).userRoles;
      }

      await kanbanTaskStore.updateTaskAssignee(selectedTask.id, applicationStore.user?.id);
      message.success(t('AssignedToYou'));
      const refreshed = await kanbanTaskStore.ApiStore.KanbanTaskApiClient.getTaskDetails(selectedTask.id, projectId || '');
      if (refreshed) setSelectedTask(refreshed);
    } catch (error) {
      console.error('Error assigning task to self:', error);
      message.error(t('FailedToAssign'));
    } finally {
      setIsSavingAssignee(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!selectedTask || !editedStatusId) return;

    try {
      const store = isAnonymous ? anonymousStore : kanbanTaskStore;
      await store.updateTaskStatus(selectedTask.id, editedStatusId);
      const updatedTask = { ...selectedTask, statusId: editedStatusId };
      setSelectedTask(updatedTask);
      setIsEditingStatus(false);
      message.success(t('TaskStatusUpdated'));
    } catch (err) {
      console.error(err);
      message.error(t('FailedToUpdateStatus'));
    }
  };

  const handleAddComment = async (comment: string): Promise<boolean> => {
    if (!comment.trim() || !selectedTask) return false;
    try {
      setIsCommentLoading(true);
      const userId = applicationStore.user?.id || null;

      const isRichText = comment.startsWith('{') && comment.endsWith('}');
      if (isAnonymous) {
        await kanbanTaskStore.ApiStore.AnonymousApiClient.createComment({
          content: comment,
          taskId: selectedTask.id
        })
      } else {
        await kanbanTaskStore.ApiStore.KanbanTaskApiClient.createComment({
          content: comment,
          taskId: selectedTask.id,
          authorId: Number(userId),
        });
      }

      const updatedComments = await getTaskComments(selectedTask.id);
      setTaskComments(updatedComments);
      message.success(t('CommentAdded'));
      return true;
    } catch (error) {
      console.error(error);
      message.error(t('FailedToAddComment'));
      return false;
    } finally {
      setIsCommentLoading(false);
    }
  };

  const handleUploadAttachment = async (file: File): Promise<boolean> => {
    if (!selectedTask) {
      message.error('No task selected');
      return false;
    }

    try {
      setIsAttachmentUploading(true);
      const taskId = selectedTask.id.toString();
      const result = await uploadTaskAttachment(taskId, file);

      if (result) {
        message.success(`${file.name} uploaded successfully`);
        const newAttachments = await getTaskAttachments(taskId);
        setAttachments(newAttachments);
        return true;
      } else {
        message.error('Upload failed');
        return false;
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      message.error(`Failed to upload: ${errorMessage}`);
      return false;
    } finally {
      setIsAttachmentUploading(false);
    }
  };

  const handleDeleteAttachment = async (publicId: string) => {
    if (!selectedTask) return;
    try {
      isAnonymous ?
        await anonymousStore.deleteAttachment(publicId, selectedTask.id.toString()) :
        await kanbanTaskStore.deleteAttachment(publicId, selectedTask.id.toString());
      message.success(t('AttachmentDeleted'));
      await getTaskAttachments(selectedTask.id.toString());
      setAttachments([...kanbanTaskStore.attachments]);
    } catch (error) {
      console.error(error);
      message.error(t('FailedToDeleteAttachment'));
    }
  };

  const handleFileChange = async (info: any) => {
    const { status, originFileObj } = info.file;

    if (status === 'uploading') {
      setIsAttachmentUploading(true);
      return;
    }

    if (status === 'done') {
      setIsAttachmentUploading(false);
      return;
    }

    if (status === 'error') {
      setIsAttachmentUploading(false);
      message.error(`${info.file.name} upload failed.`);
    }

    let newFileList = [...info.fileList];

    newFileList = newFileList.slice(-5);

    newFileList = newFileList.map(file => {
      if (file.response) {
        file.url = file.response.url;
      }
      return file;
    });

    setUploadFileList(newFileList);
  };

  const customUploadRequest = async ({ file, onSuccess, onError }: any) => {
    try {
      const success = await handleUploadAttachment(file);
      if (success) {
        onSuccess({}, new XMLHttpRequest());
      } else {
        onError(new Error('Upload failed'));
      }
    } catch (error) {
      onError(error);
    }
  };

  const [draggedTask, setDraggedTask] = useState<KanbanTask | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget>(null);
  const columnRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const cardRefs = useRef<{ [taskId: string]: HTMLDivElement | null }>({});

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, task: KanbanTask) => {
    if (isReadOnly)
      return;

    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('taskId', task.id.toString());
  };

  const handleDragEnd = () => {
    if (isReadOnly)
      return;

    setDraggedTask(null);
    setDropTarget(null);
  };

  const handleColumnDragOver = (e: React.DragEvent<HTMLDivElement>, statusId: string) => {
    if (isReadOnly)
      return;

    e.preventDefault();
    const container = columnRefs.current[statusId];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const offsetY = e.clientY - rect.top + container.scrollTop;
    const tasks = getFilteredTasks(statusId);
    let newIndex = tasks.length; // default: at end
    for (let i = 0; i < tasks.length; i++) {
      const cardEl = cardRefs.current[tasks[i].id];
      if (!cardEl) continue;
      const cardRect = cardEl.getBoundingClientRect();
      const cardTop = cardRect.top - rect.top + container.scrollTop;
      const cardHeight = cardRect.height;
      if (offsetY < cardTop + cardHeight / 2) {
        newIndex = i;
        break;
      }
    }
    setDropTarget({ statusId, index: newIndex });
  };

  const handleColumnDragEnter = (e: React.DragEvent<HTMLDivElement>, statusId: string) => {
    if (isReadOnly)
      return;

    e.preventDefault();
    if (getFilteredTasks(statusId).length === 0) {
      setDropTarget({ statusId, index: 0 });
    }
  };

  const handleColumnDrop = async (
    e: React.DragEvent<HTMLDivElement>,
    statusId: string
  ) => {
    if (isReadOnly)
      return;

    e.preventDefault();
    if (!draggedTask || dropTarget === null) return;

    try {
      const store = isAnonymous ? anonymousStore : kanbanTaskStore;
      await store.moveTask(draggedTask.id.toString(), statusId, dropTarget.index);
    } catch (err) {
      console.error('Error moving task:', err);
      message.error(t('FailedToReorder'));
    } finally {
      setDraggedTask(null);
      setDropTarget(null);
    }
  };



  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [columnDropTarget, setColumnDropTarget] = useState<ColumnDropTarget>(null);
  const columnHeaderRefs = useRef<{ [statusId: string]: HTMLDivElement | null }>({});
  const [isSavingAssignee, setIsSavingAssignee] = useState(false);

  const handleColumnHeaderDragStart = (e: React.DragEvent<HTMLDivElement>, statusId: string) => {
    setDraggedColumn(statusId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('statusId', statusId);
  };

  const handleColumnHeaderDragEnd = () => {
    setDraggedColumn(null);
    setColumnDropTarget(null);
  };

  const handleColumnHeaderDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (!draggedColumn) return;
    e.preventDefault();
    setColumnDropTarget({ index });
  };

  const handleColumnHeaderDrop = async (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    if (!draggedColumn || columnDropTarget === null) return;

    const currentIndex = (isAnonymous ? anonymousStore.projectStatuses : kanbanTaskStore.projectStatuses).findIndex(s => s.id === draggedColumn);
    if (currentIndex === -1 || currentIndex === index) {
      setDraggedColumn(null);
      setColumnDropTarget(null);
      return;
    }

    try {
      await kanbanTaskStore.updateStatusOrder(draggedColumn, index);
      message.success(t('ColumnReordered'));
    } catch (error) {
      console.error('Error reordering column:', error);
      message.error(t('FailedToReorderColumn'));
    }

    setDraggedColumn(null);
    setColumnDropTarget(null);
  };

  const [isExportTimeLogsModalVisible, setIsExportTimeLogsModalVisible] = useState(false);
  const [exportPeriod, setExportPeriod] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [exportAllLogs, setExportAllLogs] = useState<boolean>(false);

  const handleExportTimeLogs = async () => {
    if (!checkUserRolesForExport()) {
      message.error(t('NoPermissionToExport'));
      return;
    }

    const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').split('-')[0];

    const payload: ExportTimeLogsParams = {
      userId: applicationStore.user.id,
      projectId: String(selectedProject),
      startPeriod: exportPeriod?.[0]?.startOf("day").toISOString(),
      endPeriod: exportPeriod?.[1]?.endOf("day").toISOString(),
      exportAllLogs: exportAllLogs,
      language: lang,
    };

    const ok = await kanbanTaskStore.exportTimeLogsAndDownload(payload);
    if (!ok) {
      message.error(t('ExportFailed'));
      return;
    }

    setIsExportTimeLogsModalVisible(false);
    setExportPeriod(null);
    setExportAllLogs(false);
  };

  const checkUserRolesForExport = (): boolean => {
    if (applicationStore.user?.roles?.some(role => [Roles.Owner, Roles.Admin].includes(role.id))) {
      return true;
    } else if (projectsStore.project?.members
      ?.some(member => member.id === applicationStore.user?.id && (Number(member.role.id) === Roles.Admin))) {
      return true;
    }

    return false;
  }

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filterPriority !== null) count++;
    if (filterAssignee) count++;
    if (filterCreatedDate) count++;
    if (filterStartDate) count++;
    if (filterDueDate) count++;
    return count;
  }, [
    filterPriority,
    filterAssignee,
    filterCreatedDate,
    filterStartDate,
    filterDueDate
  ]);

  return (
    <div className="kanban-tasks-container">
      <div className="kanban-content kanban-font-settings">
        <Spin spinning={isShowInternalLoader && (isAnonymous ? anonymousStore.isLoading : kanbanTaskStore.isLoading)}>

          <div className="filter-bar">
            <Row justify="space-between" align="middle">
              {/* LEFT: FILTERS */}
              <Col>
                <Space>
                  <Input
                    placeholder={t('SearchByTitleOrId')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="hover-effect"
                  />
                  <Popover
                    trigger="click"
                    placement="bottomLeft"
                    overlayStyle={{ padding: 0 }}
                    content={
                      <div style={{ width: 640, padding: 16 }}>
                        <Row gutter={16}>
                          <Col span={12}>
                            <div className="filter-field">
                              <div className="filter-label">{t('FilterByAssignee')}</div>
                              <Select
                                allowClear
                                value={filterAssignee || undefined}
                                onChange={(value) => handleFilterAssigneeChange(value ?? null)}
                                showSearch
                                optionFilterProp="children"
                                style={{ width: '100%' }}
                              >
                                {employeesStore.users
                                  .slice()
                                  .sort((a, b) => {
                                    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                                    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                                    return nameA.localeCompare(nameB);
                                  })
                                  .map((user) => (
                                    <Option key={user.id} value={String(user.id)}>
                                      {user.firstName} {user.lastName}
                                    </Option>
                                  ))}
                              </Select>
                            </div>
                          </Col>

                          <Col span={12}>
                            <div className="filter-field">
                              <div className="filter-label">{t('FilterByPriority')}</div>
                              <Select
                                allowClear
                                value={filterPriority !== null ? filterPriority : undefined}
                                onChange={(value) => handleFilterPriorityChange(value ?? null)}
                                style={{ width: '100%' }}
                              >
                                {priorityOptions(t).map((opt) => (
                                  <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </Option>
                                ))}
                              </Select>
                            </div>
                          </Col>
                        </Row>

                        <Row gutter={16} style={{ marginTop: 12 }}>
                          <Col span={12}>
                            <div className="filter-field">
                              <div className="filter-label">{t('StartDate')}</div>
                              <DatePicker.RangePicker
                                value={filterStartDate as any}
                                onChange={(vals) =>
                                  handleFilterStartDateChange(vals as [dayjs.Dayjs, dayjs.Dayjs] | null)
                                }
                                allowClear
                                format="YYYY-MM-DD"
                                style={{ width: '100%' }}
                              />
                            </div>
                          </Col>

                          <Col span={12}>
                            <div className="filter-field">
                              <div className="filter-label">{t('DueDate')}</div>
                              <DatePicker.RangePicker
                                value={filterDueDate as any}
                                onChange={(vals) =>
                                  handleFilterDueDateChange(vals as [dayjs.Dayjs, dayjs.Dayjs] | null)
                                }
                                allowClear
                                format="YYYY-MM-DD"
                                style={{ width: '100%' }}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row gutter={16} style={{ marginTop: 12 }}>
                          <Col span={12}>
                            <div className="filter-field">
                              <div className="filter-label">{t('CreatedAt')}</div>
                              <DatePicker.RangePicker
                                value={filterCreatedDate as any}
                                onChange={(vals) =>
                                  handleFilterCreatedDateChange(vals as [dayjs.Dayjs, dayjs.Dayjs] | null)
                                }
                                allowClear
                                format="YYYY-MM-DD"
                                style={{ width: '100%' }}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row justify="end" style={{ marginTop: 16 }}>
                          <Button onClick={handleClearFilters}>
                            {t('ClearFilters')}
                          </Button>
                        </Row>
                      </div>
                    }
                  >
                    <Button className="hover-effect">
                      {t('Filters')}
                      {activeFiltersCount > 0 && (
                        <Badge count={activeFiltersCount} style={{ marginLeft: 6 }} />
                      )}
                    </Button>
                  </Popover>

                  {!isAnonymous && checkUserRolesForExport() && (
                    <Button
                      onClick={() => setIsExportTimeLogsModalVisible(true)}
                      className="hover-effect"
                    >
                      {t("ExportTime")}
                    </Button>
                  )}
                </Space>
              </Col>

              {/* RIGHT: ACTIONS (AS BEFORE) */}
              <Col>
                <Space>
                  <Radio.Group
                    value={viewMode}
                    onChange={(e) => handleViewChange(e.target.value)}
                    buttonStyle="solid"
                    className="view-mode-toggle"
                  >
                    <Radio.Button value="kanban" className="hover-effect">
                      <AppstoreFilled size={16} className="view-mode-icon" />
                      {t('Kanban')}
                    </Radio.Button>
                    <Radio.Button value="list" className="hover-effect">
                      <List size={16} className="view-mode-icon" />
                      {t('List')}
                    </Radio.Button>
                  </Radio.Group>
                </Space>
              </Col>
            </Row>
          </div>

          <div className="project-header kanban-headers">
            <Space size="large">
              <Title level={4} className="project-title">
                {projectsStore.project?.title || 'Project'}
              </Title>
            </Space>
          </div>

          {isReadOnly && (
            <div className="read-only-banner">
              <InfoCircleOutlined style={{ marginRight: 8 }} />
              <Text type="secondary">
                {t('ViewOnlyAccess')} - {t('YouHaveReadOnlyPermissions')}
              </Text>
            </div>
          )}

          {viewMode === 'kanban' ? (
            <div className="kanban-board">
              {((isAnonymous ? anonymousStore.projectStatuses : kanbanTaskStore.projectStatuses)?.length ?? 0) === 0 ? (
                <div className="empty-columns">
                  <div className="empty-columns-message">
                    <Title level={4}>{t('NoColumnsYet')}</Title>
                    <Paragraph className="empty-description">
                      {t('CreateYourFirstColumnToGetStarted')}
                    </Paragraph>
                    <Button
                      type="primary"
                      size="large"
                      icon={<Plus size={16} />}
                      onClick={() => setIsAddColumnModalVisible(true)}
                      className="empty-add-column-button hover-effect"
                    >
                      {t('AddStatus')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="columns-container">
                  {(isAnonymous ? anonymousStore.projectStatuses : kanbanTaskStore.projectStatuses)
                    .slice()
                    .sort((a, b) => a.index - b.index)
                    .map((status, statusIndex) => {
                      const filteredTasks = getFilteredTasks(status.id);
                      return (
                        <div key={status.id} className="column-wrapper">
                          {columnDropTarget?.index === statusIndex && draggedColumn && (
                            <div className="column-drop-indicator" />
                          )}
                          <div
                            className={`column ${dropTarget?.statusId === status.id ? 'column-drop-target' : ''}`}
                            onDragOver={(e) => handleColumnHeaderDragOver(e, statusIndex)}
                            onDrop={(e) => handleColumnHeaderDrop(e, statusIndex)}
                          >
                            <div
                              ref={(el) => (columnHeaderRefs.current[status.id] = el)}
                              className="column-header hover-effect"
                              draggable
                              onDragStart={(e) => handleColumnHeaderDragStart(e, status.id)}
                              onDragEnd={handleColumnHeaderDragEnd}
                              style={{
                                minHeight: editingColumnId === status.id ? "130px" : 'auto'
                              }}
                            >
                              {editingColumnId === status.id ? (
                                <div className="column-edit-form">
                                  <Input
                                    value={editedColumnName}
                                    onChange={(e) => setEditedColumnName(e.target.value)}
                                    autoFocus
                                    className="column-edit-input hover-effect"
                                  />
                                  <Checkbox
                                    checked={editedIsFinalStatus}
                                    onChange={(e) => setEditedIsFinalStatus(e.target.checked)}
                                    style={{ marginTop: 8 }}
                                  >
                                    {t('IsFinalStatus')}
                                    <Tooltip title={t('FinalStatusTooltip')}>
                                      <InfoCircleOutlined style={{ marginLeft: 4 }} />
                                    </Tooltip>
                                  </Checkbox>
                                  <div className="column-edit-actions">
                                    <Button
                                      type="primary"
                                      size="small"
                                      onClick={handleSaveColumnName}
                                      className="column-save-button hover-effect"
                                    >
                                      {t('Save')}
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={handleCancelEditColumn}
                                      className="column-cancel-button hover-effect"
                                    >
                                      {t('Cancel')}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="column-title kanban-headers">
                                    {status.name} ({filteredTasks.length})
                                  </div>
                                  <div className="column-actions">
                                    <Dropdown
                                      menu={{
                                        items: [
                                          { key: 'edit', label: t('Edit'), icon: <Edit size={14} /> },
                                          ...(!isAnonymous ? [{ key: 'delete', label: t('Delete'), icon: <Trash2 size={14} />, danger: true }] : []),
                                        ],
                                        onClick: ({ key }) => {
                                          if (key === 'edit') {
                                            handleEditColumn(status.id, status.name, status.isFinalStatus);
                                          } else if (key === 'delete') {
                                            handleDeleteColumn(status.id);
                                          }
                                        }
                                      }}
                                      trigger={['click']}
                                      disabled={isReadOnly}
                                    >
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<MoreVertical size={16} />}
                                        onClick={(e) => e.preventDefault()}
                                        className="column-menu-button hover-effect"
                                      />
                                    </Dropdown>
                                  </div>
                                </>
                              )}
                            </div>

                            <div
                              ref={(el) => (columnRefs.current[status.id] = el)}
                              className="column-content"
                              onDragOver={(e) => handleColumnDragOver(e, status.id)}
                              onDragEnter={(e) => handleColumnDragEnter(e, status.id)}
                              onDrop={(e) => handleColumnDrop(e, status.id)}
                            >
                              {filteredTasks.map((task, index) => (
                                <React.Fragment key={task.id}>
                                  {dropTarget?.statusId === status.id && dropTarget.index === index && (
                                    <div className="task-drop-indicator" />
                                  )}
                                  <TaskCard
                                    task={task}
                                    onTaskClick={handleShowTaskDetails}
                                    handleDragStart={isReadOnly ? (e: React.DragEvent<HTMLDivElement>, task: KanbanTask) => { } : handleDragStart}
                                    handleDragEnd={isReadOnly ? () => { } : handleDragEnd}
                                    cardRef={(el) => (cardRefs.current[task.id] = el)}
                                    t={t}
                                  />
                                </React.Fragment>
                              ))}
                              {dropTarget?.statusId === status.id && dropTarget.index === filteredTasks.length && (
                                <div className="task-drop-indicator" />
                              )}

                              {inlineAddingTaskInStatus === status.id ? (
                                <InlineTaskForm
                                  statusId={status.id}
                                  onCancel={() => setInlineAddingTaskInStatus(null)}
                                  onSubmit={(statusId: string, title: string, description: string) =>
                                    handleQuickAddTask(statusId, title, description)
                                  }
                                />
                              ) : (
                                <Button
                                  type="dashed"
                                  icon={<Plus size={16} />}
                                  block
                                  onClick={() => {
                                    setInlineAddingTaskInStatus(status.id);
                                  }}
                                  className="add-column-task-button hover-effect"
                                  disabled={isReadOnly}
                                >
                                  {t('AddTask')}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                  <div className="add-column-wrapper">
                    <Button
                      type="dashed"
                      icon={<Plus size={16} />}
                      onClick={() => setIsAddColumnModalVisible(true)}
                      className="add-column-button hover-effect"
                      disabled={isReadOnly}
                    >
                      {t('AddStatus')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <ListView
              tasks={getAllFilteredTasks()}
              statuses={isAnonymous ? anonymousStore.projectStatuses : kanbanTaskStore.projectStatuses}
              users={employeesStore.users}
              onTaskClick={handleShowTaskDetails}
              t={t}
              showDeletedTasks={showDeletedTasks}
              setShowDeletedTasks={setShowDeletedTasks}
            />
          )}

          <Modal
            title={t('ExportTimeLogs')}
            open={isExportTimeLogsModalVisible}
            onCancel={() => setIsExportTimeLogsModalVisible(false)}
            footer={[
              <Button key="cancel" onClick={() => setIsExportTimeLogsModalVisible(false)} className="hover-effect">
                {t('Cancel')}
              </Button>,
              <Button
                key="export"
                type="primary"
                onClick={handleExportTimeLogs}
                className="hover-effect"
              >
                {t('Export')}
              </Button>,
            ]}
          >
            <Spin spinning={kanbanTaskStore.isExportingTimeLogs}>
              <Form layout="vertical">
                <Form.Item label={t('Period')}>
                  <DatePicker.RangePicker
                    value={exportPeriod as any}
                    onChange={(vals) => setExportPeriod(vals as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                    allowClear
                    format="YYYY-MM-DD"
                    className="hover-effect"
                  />
                </Form.Item>

                <Form.Item>
                  <Checkbox
                    checked={exportAllLogs}
                    onChange={(e) => setExportAllLogs(e.target.checked)}
                  >
                    {t('ExportAllProjectLogs')}
                    <Tooltip title={t('ExportAllProjectLogsHint')}>
                      <Button type="text" icon={<InfoCircleOutlined />} style={{ padding: 0, marginLeft: 4 }} />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Form>
            </Spin>
          </Modal>

          <Modal
            title={t('AddStatus')}
            open={isAddColumnModalVisible}
            onCancel={() => {
              setIsAddColumnModalVisible(false);
              setNewColumnName('');
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setIsAddColumnModalVisible(false);
                  setNewColumnName('');
                }}
                className="hover Ascending-descending order of nested comments and/or blockquotes hover-effect"
              >
                {t('Cancel')}
              </Button>,
              <Button
                key="add"
                type="primary"
                onClick={handleAddColumn}
                disabled={!newColumnName.trim()}
                className="hover-effect"
              >
                {t('Add')}
              </Button>
            ]}
            className="add-status-modal"
          >
            <Form layout="vertical" className="add-status-form">
              <Form.Item label={t('StatusName')}>
                <Input
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder={t('EnterStatusName')}
                  autoFocus
                  className="hover-effect"
                />
              </Form.Item>

              <Form.Item>
                <Checkbox
                  checked={isFinalStatus}
                  onChange={(e) => setIsFinalStatus(e.target.checked)}
                >
                  {t('IsFinalStatus')}
                  <Tooltip title={t('FinalStatusTooltip')}>
                    <Button
                      type="text"
                      icon={<InfoCircleOutlined />}
                      style={{ padding: 0, marginLeft: 4 }}
                    />
                  </Tooltip>
                </Checkbox>
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title={null}
            open={isTaskDetailsModalVisible}
            onCancel={handleCloseTaskDetails}
            width={'60%'}
            footer={null}
            closeIcon={null}
            className="task-details-modal"
          >
            {selectedTask && (
              <div className="task-details-container">
                <div className="task-details-header">
                  <div className="task-details-title">
                    <Badge
                      color={getPriorityColor(selectedTask.priority)}
                      className="task-priority-badge"
                    />
                    {selectedTask.publicId && (
                      <span className="task-public-id">
                        {selectedTask.publicId.toUpperCase()}
                      </span>
                    )}
                    <span>{t('TaskDetails')}</span>
                    {isReadOnly && (
                      <Tag color="warning" icon={<InfoCircleOutlined />}>
                        {t('ReadOnly')}
                      </Tag>
                    )}
                  </div>
                  <div className="task-details-actions">
                    {showDeletedTasks ? (
                      <Button
                        type="primary"
                        icon={<RedoOutlined size={16} />}
                        className="hover-effect"
                        onClick={async () => {
                          await kanbanTaskStore.restoreTask(selectedTask.id.toString());
                          message.success(t('TaskRestored'));
                          setIsTaskDetailsModalVisible(false);
                          setSelectedTask(null);
                          setShowDeletedTasks(false);
                        }}
                      >
                        {t('Restore')}
                      </Button>
                    ) : (
                      <Button
                        icon={<Copy size={16} />}
                        onClick={copyTaskLink}
                        className="copy-link-button hover-effect"
                      >
                        {t('CopyLink')}
                      </Button>
                    )}
                    {!isAnonymous && !isReadOnly && (
                      <Popconfirm
                        title={t('ConfirmDeleteTask')}
                        description={showDeletedTasks ? t('AreYouSureDeleteTaskPermanent') : t('AreYouSureDeleteTask')}
                        okText={t('Delete')}
                        okType="danger"
                        cancelText={t('Cancel')}
                        placement="left"
                        onConfirm={() => handleDeleteTask(selectedTask.id)}
                      >
                        <Button
                          danger
                          icon={<Trash2 size={16} />}
                          className="task-delete-button hover-effect"
                        >
                          {t('Delete')}
                        </Button>
                      </Popconfirm>
                    )}
                    <Button
                      icon={<X size={16} />}
                      onClick={handleCloseTaskDetails}
                      className="task-close-button hover-effect"
                    />
                  </div>
                </div>

                <div className="task-details-content">
                  <div className="task-details-main">
                    <Spin spinning={isAnonymous ? anonymousStore.isTitleLoading : kanbanTaskStore.isTitleLoading}>
                      <InlineEditableField
                        value={selectedTask.title}
                        onSave={handleSaveTitle}
                        label={t('Title')}
                        disabled={isReadOnly || showDeletedTasks}
                      />
                    </Spin>

                    <Spin spinning={isAnonymous ? anonymousStore.isDescriptionLoading : kanbanTaskStore.isDescriptionLoading}>
                      <div className="task-description-section">
                        <div className="section-header">
                          <Title level={5} className="section-title">
                            {t('Description')}
                          </Title>
                        </div>
                        <div className="description-content group" style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                          {isEditingDescription ? (
                            <div className="description-edit">
                              <RichTextEditor
                                initialData={editedDescriptionData}
                                onChange={(data: any) => setEditedDescriptionData(data)}
                              />
                              <Space className="description-edit-actions">
                                <Button
                                  type="primary"
                                  onClick={handleSaveDescription}
                                  className="hover-effect"
                                  disabled={isReadOnly || showDeletedTasks}
                                >
                                  {t('Save')}
                                </Button>
                                <Button
                                  onClick={() => setIsEditingDescription(false)}
                                  className="hover-effect"
                                  disabled={isReadOnly || showDeletedTasks}
                                >
                                  {t('Cancel')}
                                </Button>
                              </Space>
                            </div>
                          ) : (
                            <div
                              className={isReadOnly || showDeletedTasks ? "" : "description-view hover-effect"}
                              onClick={() => {
                                if (isReadOnly || showDeletedTasks) return;
                                setIsEditingDescription(true);
                              }}
                            >
                              <ReadOnlyEditor
                                data={safeParseEditorData(selectedTask.description)}
                                taskId={selectedTask.id}
                                disabled={isReadOnly || showDeletedTasks}
                              />
                              {(!isReadOnly) && (
                                <Button
                                  type="text"
                                  size="small"
                                  className="edit-button hover-effect"
                                  icon={<Edit size={14} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsEditingDescription(true);
                                  }}
                                  disabled={isReadOnly || showDeletedTasks}
                                />)}
                            </div>
                          )}
                        </div>
                      </div>
                    </Spin>

                    <div className="task-attachments-section">
                      <Title level={5} className="section-title">
                        {t('Attachments')}
                      </Title>

                      <Spin spinning={isAttachmentUploading}>
                        <div
                          className="attachment-upload-container"
                          style={{
                            pointerEvents: isReadOnly || showDeletedTasks ? "none" : "auto",
                            opacity: isReadOnly || showDeletedTasks ? 0.5 : 1,
                          }}
                        >
                          <Dragger
                            name="file"
                            fileList={uploadFileList}
                            onChange={handleFileChange}
                            customRequest={customUploadRequest}
                            showUploadList={{ showRemoveIcon: true }}
                            multiple={false}
                            className="attachment-dropzone"
                            beforeUpload={(file) => {
                              if (isReadOnly) {
                                message.error(t('YouHaveReadOnlyPermissions', { size: 10 }));
                                return Upload.LIST_IGNORE;
                              }

                              const isVideo = file.type.startsWith('video/');
                              const sizeLimit = isVideo ? 100 : 10;
                              const isUnderLimit = file.size / 1024 / 1024 < sizeLimit;

                              if (!isUnderLimit) {
                                message.error(t('FileSizeLimit', { size: sizeLimit }));
                                return Upload.LIST_IGNORE;
                              }

                              return true;
                            }}
                          >
                            <p className="ant-upload-drag-icon">
                              <UploadIcon size={36} className="upload-icon" />
                            </p>
                            <p className="ant-upload-text" style={{ fontSize: 14 }}>{t('ClickOrDragFileToUpload')}</p>
                            <p className="ant-upload-hint" style={{ fontSize: 13 }}>
                              {t('SupportForSingleOrBulkUpload')}
                            </p>
                          </Dragger>
                        </div>
                      </Spin>

                      {attachments.length > 0 && (
                        <div className="attachments-list">
                          <Text strong className="attachments-header">
                            {t('AttachedFiles')} ({attachments.length})
                          </Text>
                          <Spin spinning={kanbanTaskStore.isAttachmentDeleting}>
                            <div className="attachments-grid">
                              {attachments.map((att) => {
                                const fileName =
                                  att.url.split('/').pop() ||
                                  att.publicId.split('/').pop() ||
                                  att.publicId;

                                const fileExt = fileName.split('.').pop()?.toLowerCase() || '';
                                let FileIcon = FileText;

                                if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(fileExt)) {
                                  FileIcon = File;
                                }

                                return (
                                  <div key={att.publicId} className="attachment-card">
                                    <div className="attachment-card-content">
                                      <div className="attachment-icon">
                                        <FileIcon size={24} />
                                      </div>
                                      <div className="attachment-details">
                                        <Tooltip title={fileName}>
                                          <a
                                            href={att.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="attachment-filename"
                                          >
                                            {fileName.length > 20 ? `${fileName.substring(0, 17)}...` : fileName}
                                          </a>
                                        </Tooltip>
                                        <div className="attachment-actions">
                                          <Button
                                            type="text"
                                            size="small"
                                            className="attachment-view-button hover-effect"
                                            onClick={() => window.open(att.url, '_blank')}
                                          >
                                            {t("View")}
                                          </Button>
                                          <Button
                                            type="text"
                                            danger
                                            size="small"
                                            onClick={() => handleDeleteAttachment(att.publicId)}
                                            className={isReadOnly || showDeletedTasks ? "" : "attachment-delete-button hover-effect"}
                                            disabled={isReadOnly || showDeletedTasks}
                                          >
                                            {t("Delete")}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </Spin>
                        </div>
                      )}

                      {attachments.length === 0 && !isAttachmentUploading && (
                        <div className="no-attachments-message" style={{ marginTop: 15 }}>
                          <Paperclip size={24} className="no-attachments-icon" />
                          <Text type="secondary">{t('NoAttachmentsYet')}</Text>
                        </div>
                      )}
                    </div>

                    <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as 'comments' | 'changes')} className="task-tabs">
                      <TabPane tab={t('Comments')} key="comments">
                        <Spin spinning={isCommentLoading}>
                          <RichTextComments
                            comments={taskComments}
                            onAddComment={handleAddComment}
                            selectedTaskId={String(selectedTask.id)}
                            isReadOnly={isReadOnly || showDeletedTasks}
                            t={t}
                          />
                        </Spin>
                      </TabPane>
                      <TabPane tab={t('Changes')} key="changes">
                        <div className="changes-list">
                          {kanbanTaskStore.taskChanges && kanbanTaskStore.taskChanges.length > 0 ? (
                            <table className="changes-table">
                              <thead>
                                <tr>
                                  <th className="changes-col-small">Date</th>
                                  <th className="changes-col-small">Property</th>
                                  <th className="changes-col-large">Old Value</th>
                                  <th className="changes-col-large">New Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {kanbanTaskStore.taskChanges.map((chg: KanbanTaskChange) => {
                                  const formatValue = (val: string | undefined | null) => {
                                    if (!val) return '';
                                    if (/^\d{4}-\d{2}-\d{2}T/.test(val)) return dayjs(val).format('LLL');
                                    if (val.startsWith('{') && val.includes('"blocks":')) {
                                      try {
                                        const parsed = JSON.parse(val);
                                        if (parsed.blocks && Array.isArray(parsed.blocks)) {
                                          return parsed.blocks.map((b: any) => {
                                            if (b.data?.text) return b.data.text;
                                            if (b.data?.items) return b.data.items.map((i: any) => i.content || i).join(', ');
                                            return '';
                                          }).filter(Boolean).join(' ').replace(/<[^>]+>/g, '');
                                        }
                                      } catch (e) {
                                      }
                                    }
                                    return val;
                                  };

                                  return (
                                    <tr key={chg.id}>
                                      <td>
                                        {dayjs(chg.createdDate).format('LLL')}
                                      </td>
                                      <td>
                                        {chg.propertyName}
                                      </td>
                                      <td>
                                        <div className="changes-value-container">
                                          {formatValue(chg.previousValue)}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="changes-value-container">
                                          {formatValue(chg.newValue)}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          ) : (
                            <p className="no-changes">{t('NoChangesRecorded')}</p>
                          )}
                        </div>
                      </TabPane>
                      <TabPane tab={t('Time')} key="time">
                        <TimeTabContent
                          selectedTask={selectedTask}
                          taskTimeMetrics={kanbanTaskStore.taskTimeMetrics}
                          taskTimeLogs={kanbanTaskStore.taskTimeLogs}
                          onLogTime={handleLogTime}
                          onUpdateEstimatedTime={handleUpdateEstimatedTime}
                          timeLoggingForm={timeLoggingForm}
                          estimatedTimeForm={estimatedTimeForm}
                          isLoggingTime={isLoggingTime}
                          setIsLoggingTime={setIsLoggingTime}
                          isEditingEstimatedTime={isEditingEstimatedTime}
                          setIsEditingEstimatedTime={setIsEditingEstimatedTime}
                          t={t}
                          projectName={projectsStore.project?.title || 'current'}
                          isReadOnly={isReadOnly}
                        />
                      </TabPane>
                    </Tabs>
                  </div>

                  <div className="task-details-sidebar">
                    <div className="task-detail-field" style={{ pointerEvents: isReadOnly || showDeletedTasks ? 'none' : 'auto' }}>
                      <div className="field-header">
                        <Title level={5} className="field-title">
                          {t('Status')}
                        </Title>
                      </div>
                      <Spin spinning={isAnonymous ? anonymousStore.isStatusLoading : kanbanTaskStore.isStatusLoading}>
                        {isEditingStatus ? (
                          <div className="field-edit">
                            <Select
                              id="task-status-select"
                              className="field-select hover-effect"
                              value={editedStatusId}
                              onChange={(val) => setEditedStatusId(val)}
                            >
                              {(isAnonymous ? anonymousStore.projectStatuses : kanbanTaskStore.projectStatuses).map((status) => (
                                <Option key={status.id} value={status.id}>
                                  {status.name}
                                </Option>
                              ))}
                            </Select>
                            <div className="field-actions">
                              <Button
                                type="primary"
                                size="small"
                                onClick={handleSaveStatus}
                                className="field-save hover-effect"
                              >
                                {t('Save')}
                              </Button>
                              <Button
                                size="small"
                                onClick={() => setIsEditingStatus(false)}
                                className="field-cancel hover-effect"
                              >
                                {t('Cancel')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="field-view hover-effect group"
                            onClick={() => {
                              setIsEditingStatus(true);
                            }}
                          >
                            <Tag color="blue" className="status-tag">
                              {(isAnonymous ? anonymousStore.projectStatuses : kanbanTaskStore.projectStatuses).find(s => s.id === selectedTask.statusId)?.name || t('Unknown')}
                            </Tag>
                            {!isAnonymous && (
                              <Button
                                type="text"
                                size="small"
                                className={`edit-button ${!isAnonymous ? "hover-effect" : ""}`}
                                icon={<Edit size={14} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsEditingStatus(true);
                                }}
                              />
                            )}
                          </div>
                        )}
                      </Spin>
                    </div>

                    <div className="task-detail-field" style={{ pointerEvents: isReadOnly || showDeletedTasks ? 'none' : 'auto' }}>
                      <div className="field-header">
                        <Title level={5} className="field-title">
                          {t('Schedule&Priority')}
                        </Title>
                      </div>
                      <Spin spinning={isAnonymous ? anonymousStore.isScheduledAndPriorityLoading : kanbanTaskStore.isScheduledAndPriorityLoading}>
                        {isEditingDatesPriority ? (
                          <div className="field-edit">
                            <Form layout="vertical" className="dates-priority-form">
                              <Form.Item label={t('StartDate')} className="date-form-item" style={{ minWidth: 100 }}>
                                <DatePicker
                                  className="date-picker hover-effect"
                                  value={editedDatesPriority.startDate}
                                  maxDate={editedDatesPriority.dueDate}
                                  onChange={(date) => setEditedDatesPriority((prev) => ({ ...prev, startDate: date }))}
                                />
                              </Form.Item>
                              <Form.Item label={t('DueDate')} className="date-form-item" style={{ minWidth: 100 }}>
                                <DatePicker
                                  className="date-picker hover-effect"
                                  allowClear
                                  value={editedDatesPriority.dueDate}
                                  minDate={editedDatesPriority.startDate}
                                  onChange={(date) => setEditedDatesPriority((prev) => ({ ...prev, dueDate: date }))}
                                />
                              </Form.Item>
                              <Form.Item label={t('Priority')} className="priority-form-item">
                                <Select
                                  className="priority-select hover-effect"
                                  value={editedDatesPriority.priority}
                                  onChange={(value) => setEditedDatesPriority((prev) => ({ ...prev, priority: value }))}
                                >
                                  {priorityOptions(t).map((opt) => (
                                    <Option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Form>
                            <div className="field-actions">
                              <Button
                                type="primary"
                                onClick={handleSaveDatesPriority}
                                className="field-save hover-effect"
                              >
                                {t('Save')}
                              </Button>
                              <Button
                                onClick={() => setIsEditingDatesPriority(false)}
                                className="field-cancel hover-effect"
                              >
                                {t('Cancel')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="field-view dates-priority-view hover-effect group"
                            onClick={() => setIsEditingDatesPriority(true)}
                          >
                            <div className="date-item">
                              <strong style={{ minWidth: 100 }}>{t('StartDate')}:</strong>{' '}
                              <span className="date-value">
                                {selectedTask.startDate ? dayjs(selectedTask.startDate).format('LL') : '-'}
                              </span>
                            </div>
                            <div className="date-item">
                              <strong style={{ minWidth: 100 }}>{t('DueDate')}:</strong>{' '}
                              <span className="date-value">
                                {selectedTask.dueDate ? dayjs(selectedTask.dueDate).format('LL') : '-'}
                              </span>
                            </div>
                            <div className="priority-item">
                              <strong>{t('Priority')}:</strong>{' '}
                              <span className="priority-value">
                                <span className="priority-icon">{getPriorityIcon(selectedTask.priority)}</span>
                                {selectedTask.priority === 0
                                  ? t('Low')
                                  : selectedTask.priority === 1
                                    ? t('Medium')
                                    : t('High')}
                              </span>
                            </div>
                            <Button
                              type="text"
                              size="small"
                              className="edit-button hover-effect"
                              icon={<Edit size={14} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsEditingDatesPriority(true);
                              }}
                              disabled={isReadOnly}
                            />
                          </div>
                        )}
                      </Spin>
                    </div>

                    <div className="task-detail-field" style={{ pointerEvents: isReadOnly || showDeletedTasks ? 'none' : 'auto' }}>
                      <div className="field-header">
                        <Title level={5} className="field-title">
                          {t('Assignee')}
                        </Title>
                      </div>

                      <Spin spinning={isSavingAssignee}>
                        <AssigneeSelector
                          selectedTask={selectedTask}
                          isEditing={isEditingAssignee}
                          setIsEditing={setIsEditingAssignee}
                          editedAssigneeId={editedAssigneeId}
                          setEditedAssigneeId={setEditedAssigneeId}
                          handleSave={handleSaveAssignee}
                          t={t}
                          users={employeesStore.users
                            .slice()
                            .sort((a, b) => {
                              const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                              const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                              return nameA.localeCompare(nameB);
                            })}
                          disabled={isAnonymous || isReadOnly}
                        />
                        {!isEditingAssignee && applicationStore.user?.id !== selectedTask?.assigneeId && !isAnonymous && !isReadOnly && !showDeletedTasks && (
                          <Button
                            type="link"
                            onClick={assignCurrentUser}
                            className="hover-effect"
                            style={{
                              paddingLeft: 1,
                              paddingRight: 1
                            }}
                            disabled={isReadOnly}
                          >
                            {t('AssignToMe')}
                          </Button>
                        )}
                      </Spin>
                    </div>

                    <div style={{ pointerEvents: isReadOnly || showDeletedTasks ? 'none' : 'auto' }}>
                      <TaskCustomFields task={selectedTask} t={t} isAnonymous={isAnonymous} />
                    </div>

                    {selectedTask.createdDate && (
                      <div className="task-detail-field">
                        <Title level={5} className="field-title">
                          {t('CreatedAt')}
                        </Title>
                        <div className="field-view">
                          <Text type="secondary" className="created-date">
                            {dayjs(selectedTask.createdDate).format('LLL')}
                          </Text>
                        </div>
                      </div>
                    )}
                    {selectedTask.createdByUserId && (
                      <div>
                        <Text type="secondary" className="created-date">
                          {`${t('Author')}`}
                        </Text>
                        {getUserComponent(
                          applicationStore.siteConfig.baseCloudinary, employeesStore.users.find(user => user.id === selectedTask.createdByUserId), applicationStore.company?.Tenant ?? 'anonymous', 30)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Modal>
        </Spin>
      </div >
    </div >
  );
});

export default KanbanTasks;